/**
 * Runtime Requests: Unified Request Execution Wrapper
 *
 * Provides standardized request execution with tracing, timing,
 * correlation IDs, structured logging, and normalized errors.
 *
 * @platform-infrastructure
 */

import { RuntimeContext, RuntimeExecution, RuntimeError } from '../contracts'
import { RuntimeContextManager } from '../context/manager'
import { RuntimeLogger, LogCategory } from '../logging/logger'
import { RuntimeErrorNormalizer } from '../errors/normalizer'

/**
 * Request Execution Options
 */
export interface RequestExecutionOptions {
  /** Request timeout in milliseconds */
  timeout?: number

  /** Number of retries */
  retries?: number

  /** Retry delay in milliseconds */
  retryDelay?: number

  /** Custom context metadata */
  metadata?: Record<string, any>

  /** Service name for logging */
  service?: string

  /** Domain name for logging */
  domain?: string

  /** Skip logging for this request */
  skipLogging?: boolean
}

/**
 * Request Execution Result
 */
export interface RequestExecutionResult<T> {
  /** Execution result */
  success: boolean

  /** Result data (if successful) */
  data?: T

  /** Normalized error (if failed) */
  error?: RuntimeError

  /** Execution metadata */
  execution: RuntimeExecution

  /** Execution duration */
  duration: number
}

/**
 * Runtime Request Executor
 *
 * Unified request execution wrapper with full runtime instrumentation.
 */
export class RuntimeRequestExecutor {
  private static defaultOptions: RequestExecutionOptions = {
    timeout: 30000, // 30 seconds
    retries: 0,
    retryDelay: 1000, // 1 second
  }

  /**
   * Execute a request with full runtime instrumentation
   */
  static async execute<T>(
    operation: string,
    requestFn: () => Promise<T>,
    options: RequestExecutionOptions = {}
  ): Promise<RequestExecutionResult<T>> {
    const opts = { ...this.defaultOptions, ...options }
    const startTime = new Date()

    // Create execution context
    const context = RuntimeContextManager.createContext(
      null, // actor (will be set by auth context)
      null, // route (will be set by caller)
      opts.service || 'runtime',
      opts.domain || 'requests',
      opts.metadata
    )

    // Create execution record
    const execution: RuntimeExecution = {
      context,
      type: 'request',
      operation,
      startTime,
      status: 'running',
    }

    // Set current context
    RuntimeContextManager.setCurrentContext(context)

    try {
      // Log execution start
      if (!opts.skipLogging) {
        RuntimeLogger.executionStart(operation, context, {
          timeout: opts.timeout,
          retries: opts.retries,
        })
      }

      // Execute with timeout and retries
      const result = await this.executeWithRetry(requestFn, opts)

      // Mark execution as completed
      execution.endTime = new Date()
      execution.duration = execution.endTime.getTime() - startTime.getTime()
      execution.status = 'completed'
      execution.result = 'success'

      // Log execution completion
      if (!opts.skipLogging) {
        RuntimeLogger.executionComplete(operation, context, execution.duration, {
          dataSize: this.getDataSize(result),
        })
      }

      return {
        success: true,
        data: result,
        execution,
        duration: execution.duration,
      }

    } catch (error) {
      // Mark execution as failed
      execution.endTime = new Date()
      execution.duration = execution.endTime.getTime() - startTime.getTime()
      execution.status = 'failed'
      execution.result = 'failure'

      // Normalize error
      const normalizedError = RuntimeErrorNormalizer.normalize(error, {
        operation,
        executionId: context.executionId,
        correlationId: context.correlationId,
      })

      execution.error = normalizedError

      // Log execution error
      if (!opts.skipLogging) {
        RuntimeLogger.executionError(operation, context, normalizedError.originalError || new Error(normalizedError.message), {
          errorCode: normalizedError.code,
          errorCategory: normalizedError.category,
          retryable: normalizedError.retryable,
        })
      }

      return {
        success: false,
        error: normalizedError,
        execution,
        duration: execution.duration,
      }

    } finally {
      // Clear current context
      RuntimeContextManager.clearCurrentContext()
    }
  }

  /**
   * Execute request with retry logic
   */
  private static async executeWithRetry<T>(
    requestFn: () => Promise<T>,
    options: RequestExecutionOptions
  ): Promise<T> {
    const { timeout, retries = 0, retryDelay = 1000 } = options
    let lastError: any

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (timeout) {
          return await this.executeWithTimeout(requestFn, timeout)
        } else {
          return await requestFn()
        }
      } catch (error) {
        lastError = error

        // Check if error is retryable
        const normalizedError = RuntimeErrorNormalizer.normalize(error)
        if (!normalizedError.retryable || attempt >= retries) {
          throw error
        }

        // Wait before retry
        if (attempt < retries) {
          await this.delay(retryDelay * Math.pow(2, attempt)) // Exponential backoff
        }
      }
    }

    throw lastError
  }

  /**
   * Execute request with timeout
   */
  private static async executeWithTimeout<T>(
    requestFn: () => Promise<T>,
    timeout: number
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timed out after ${timeout}ms`))
      }, timeout)

      requestFn()
        .then((result) => {
          clearTimeout(timeoutId)
          resolve(result)
        })
        .catch((error) => {
          clearTimeout(timeoutId)
          reject(error)
        })
    })
  }

  /**
   * Delay helper
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Get data size for logging
   */
  private static getDataSize(data: any): number {
    try {
      return JSON.stringify(data).length
    } catch {
      return 0
    }
  }

  /**
   * Execute HTTP request with full instrumentation
   */
  static async executeHttp<T>(
    method: string,
    url: string,
    requestFn: () => Promise<T>,
    options: RequestExecutionOptions = {}
  ): Promise<RequestExecutionResult<T>> {
    const context = RuntimeContextManager.getCurrentContext()
    const operation = `${method} ${url}`

    // Log request start
    if (!options.skipLogging && context) {
      RuntimeLogger.requestStart(method, url, context)
    }

    const startTime = Date.now()

    try {
      const result = await this.execute(operation, requestFn, {
        ...options,
        service: options.service || 'http',
        domain: options.domain || 'gateway',
      })

      // Log request completion
      if (!options.skipLogging && context) {
        RuntimeLogger.requestComplete(
          method,
          url,
          200, // Assume success for now
          Date.now() - startTime,
          context
        )
      }

      return result

    } catch (error) {
      // Log request error
      if (!options.skipLogging && context) {
        RuntimeLogger.requestError(method, url, error as Error, context)
      }

      throw error
    }
  }
}